import { memo, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PicklistValue } from '@/api/client';
import { useUpdateTicket } from '@/api/hooks';
import { priorityColor, priorityDot, statusColor, statusDot } from '@/lib/status-colors';

interface Props {
  ticketId: number;
  field: 'status' | 'priority' | 'queueID' | 'issueType' | 'subIssueType';
  value: number | null;
  options: PicklistValue[];
  className?: string;
  variant?: 'badge' | 'text';
  /**
   * Extra fields merged into the PATCH body whenever the user picks a new value.
   * Use case: changing issueType also clears subIssueType in the same request.
   */
  extraFields?: Record<string, number | string | null>;
}

const TRIGGER_CLS = cn(
  'inline-flex max-w-full items-center gap-1 rounded-md text-left',
  'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
);

/** Keys that open a Radix DropdownMenu from its trigger. */
export const MENU_OPEN_KEYS = new Set(['Enter', ' ', 'ArrowDown']);

/**
 * Picklist field editor. Lazy: until first used it is a plain button with the label — no Radix menu, no
 * mutation observer (a ticket grid has ~1,000 of these). The first pointerdown / Enter / Space / ArrowDown
 * mounts the real menu already open, so one click still opens it.
 */
export const InlineEdit = memo(function InlineEdit(props: Props) {
  const [active, setActive] = useState(false);
  if (active) return <InlineEditMenu {...props} />;

  const { field, value, options, className, variant = 'badge' } = props;
  const label = labelFor(options, value);
  return (
    <button
      type="button"
      className={cn(TRIGGER_CLS, className)}
      title={label}
      aria-haspopup="menu"
      aria-expanded={false}
      onPointerDown={(e: PointerEvent) => {
        // Same condition Radix's trigger opens on.
        if (e.button !== 0 || e.ctrlKey) return;
        e.preventDefault();
        setActive(true);
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (!MENU_OPEN_KEYS.has(e.key)) return;
        e.preventDefault();
        setActive(true);
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <FieldLabel field={field} variant={variant} colorClass={colorFor(field, value)} label={label} pending={false} />
    </button>
  );
});

function InlineEditMenu({ ticketId, field, value, options, className, variant = 'badge', extraFields }: Props) {
  const mutation = useUpdateTicket();
  const label = labelFor(options, value);

  const onSelect = (next: number) => {
    if (next === value) return;
    const prevLabel = label;
    mutation.mutate(
      { id: ticketId, fields: { [field]: next, ...(extraFields ?? {}) } },
      {
        onSuccess: () => {
          const nextLabel = options.find((o) => Number(o.value) === next)?.label ?? `#${next}`;
          toast.success(`${field} → ${nextLabel}`, { description: `Ticket #${ticketId} (was ${prevLabel})` });
        },
        onError: (err) => {
          toast.error(`Failed to update ${field}`, { description: err instanceof Error ? err.message : 'Unknown error' });
        },
      },
    );
  };

  // Mounted by the first click/key on the lazy button, so it starts open (and focus returns here on close).
  return (
    <DropdownMenu defaultOpen>
      <DropdownMenuTrigger
        className={cn(TRIGGER_CLS, mutation.isPending && 'opacity-60', className)}
        onClick={(e) => e.stopPropagation()}
        disabled={mutation.isPending}
        title={label}
      >
        <FieldLabel field={field} variant={variant} colorClass={colorFor(field, value)} label={label} pending={mutation.isPending} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()} className="max-h-72 overflow-auto">
        {options.length === 0 && <DropdownMenuItem disabled>No options</DropdownMenuItem>}
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onSelect={() => onSelect(Number(o.value))}
            className={cn(Number(o.value) === value && 'bg-accent/60 font-medium')}>
            <OptionLabel field={field} value={Number(o.value)} label={o.label} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Dot({ color }: { color: string }) {
  return <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />;
}

/** Priority = a coloured dot + plain label (the `--pri-*` tokens, same as PriorityTag), not a filled pill. */
function PriorityDot({ label }: { label: string }) {
  return <Dot color={priorityDot(label)} />;
}

/** A menu option: status / priority get their colour dot (dropdowns show dots, never the full pill). */
function OptionLabel({ field, value, label }: { field: Props['field']; value: number; label: string }) {
  if (field === 'status') return <span className="flex items-center gap-2"><Dot color={statusDot(value)} />{label}</span>;
  if (field === 'priority') return <span className="flex items-center gap-2"><PriorityDot label={label} />{label}</span>;
  return <>{label}</>;
}

function FieldLabel({ field, variant, colorClass, label, pending }: { field: Props['field']; variant: 'badge' | 'text'; colorClass: string; label: string; pending: boolean }): ReactNode {
  if (field === 'priority' && label !== '—') {
    return (
      <span className="flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/85">
        {pending ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : <PriorityDot label={label} />}
        <span className="truncate">{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
      </span>
    );
  }
  const inner = (
    <>
      {pending ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : null}
      <span className="truncate">{label}</span>
      <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
    </>
  );
  return variant === 'badge' ? (
    <Badge variant="secondary" className={cn('flex max-w-full items-center gap-1 border border-transparent', colorClass)}>{inner}</Badge>
  ) : (
    <span className={cn('flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-sm', colorClass)}>{inner}</span>
  );
}

function labelFor(options: PicklistValue[], value: number | null): string {
  const current = options.find((o) => Number(o.value) === value);
  return current?.label ?? (value == null ? '—' : `#${value}`);
}

function colorFor(field: Props['field'], value: number | null): string {
  return field === 'status' ? statusColor(value) : field === 'priority' ? priorityColor(value) : '';
}
